import intlTelInput from 'intl-tel-input';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import 'intl-tel-input/build/css/intlTelInput.css';
import useIPGeolocation from '@/hooks/useIPGeolocation';
import logger from '@/providers/Logging';

import classNames from 'classnames';

import { Input, InputOnBlur } from '../Input';

import s from './PhoneInput.module.scss';
import validate from './validate';

type Iti = ReturnType<typeof intlTelInput>;

type Props = {
  id: string;
  value: string | null;
  placeholder?: string;
  onChange: (phoneNumber: string, country: string, nationalPhoneNumber?: string) => void;
  onError?: (error: string | null) => void;
  onFocus?: () => void;
  label: string;
  disabled?: boolean;
  required?: boolean;
  error?: string | null;
  isTouched?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
  allowInvalidPhoneNumber?: boolean;
  notInQuestionnaire?: boolean;
  triggerOnChangeOnBlur?: boolean;
};

const PREFERRED_COUNTRIES = ['us', 'gb', 'au', 'de', 'se', 'ie', 'nl'];

const PhoneInput: React.FC<Props> = ({
  id,
  disabled,
  value: inputValue,
  placeholder,
  label,
  onChange,
  onFocus,
  error,
  onError,
  required = false,
  onBlur,
  isTouched: isTouchedExternal,
  allowInvalidPhoneNumber = false,
  notInQuestionnaire = false,
  triggerOnChangeOnBlur = false,
}) => {
  const value = inputValue ?? '';
  const [internalValue, setInternalValue] = useState(value);
  const [internalError, setInternalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const itiRef = useRef<Iti | null>(null);
  const [itiIsReady, setItiIsReady] = useState(false);
  const [isTouchedInternal, setIsTouchedInternal] = useState(false);
  const isTouched = isTouchedExternal || isTouchedInternal;

  const onChangeRef = useRef<Props['onChange'] | null>();
  onChangeRef.current = onChange;

  const onBlurRef = useRef<Props['onBlur'] | null>();
  onBlurRef.current = onBlur;

  const onErrorRef = useRef<Exclude<Props['onError'], undefined> | null>();
  onErrorRef.current = onError;
  const [geolocation] = useIPGeolocation();
  const isoCode = geolocation?.country?.iso_code;
  const inputElement = inputRef.current;

  useEffect(() => {
    if (inputElement) {
      try {
        itiRef.current = intlTelInput(inputElement, {
          autoPlaceholder: 'off',
          countryOrder: PREFERRED_COUNTRIES,
          dropdownContainer: document.body,
          loadUtils: () => import('intl-tel-input/utils'),
          nationalMode: false,
        });
        itiRef.current.promise
          .then(() => setItiIsReady(true))
          .catch((err1) => {
            logger.error('Phone input error', { error: err1?.toString(), flow: 'other' });
          });
      } catch (err2) {
        logger.error('Phone input error', { error: err2?.toString(), flow: 'other' });
      }
    }
  }, [inputElement]);

  useEffect(() => {
    if (itiIsReady && itiRef.current && intlTelInput.utils) {
      const phoneNumber = itiRef.current.getNumber();
      const nationalPhoneNumber = itiRef.current.getNumber(intlTelInput.utils.numberFormat.NATIONAL);
      setInternalValue(phoneNumber);
      if (onChangeRef.current) {
        const countryData = itiRef.current.getSelectedCountryData();
        const country = (countryData.iso2 || '').toUpperCase();
        onChangeRef.current(phoneNumber, country, nationalPhoneNumber);
      }
    }
  }, [itiIsReady]);

  const countryChangeListener = useMemo(
    () => () => {
      if (itiIsReady && itiRef.current && intlTelInput.utils) {
        const phoneNumber = itiRef.current.getNumber();
        const nationalPhoneNumber = itiRef.current.getNumber(intlTelInput.utils.numberFormat.NATIONAL);
        setInternalValue(phoneNumber);
        if (onChangeRef.current) {
          const countryData = itiRef.current.getSelectedCountryData();
          const country = (countryData.iso2 || '').toUpperCase();
          onChangeRef.current(phoneNumber, country, nationalPhoneNumber);
        }
      }
    },
    [itiIsReady],
  );

  useEffect(() => {
    if (inputElement) {
      inputElement.addEventListener('countrychange', countryChangeListener);
    }

    return () => inputElement?.removeEventListener('countrychange', countryChangeListener);
  }, [inputElement, countryChangeListener]);

  useEffect(() => {
    if (isoCode && itiRef.current && !inputValue) {
      itiRef.current.setCountry(isoCode);
      if (onErrorRef.current) {
        onErrorRef.current(null);
      }
    }
  }, [isoCode, inputValue]);

  useEffect(() => {
    const newValue = inputValue ?? '';

    // Only update if the value actually changed
    if (internalValue !== newValue) {
      setInternalValue(newValue);

      // Only call setNumber if the intl-tel-input value is different to avoid cursor jumps
      if (itiRef.current) {
        const currentNumber = itiRef.current.getNumber();
        if (currentNumber !== newValue) {
          itiRef.current.setNumber(newValue);
        }
      }
    }
  }, [inputValue, internalValue]);

  useEffect(() => {
    if (itiIsReady && itiRef.current) {
      const countryData = itiRef.current.getSelectedCountryData();
      const countryCode = countryData?.dialCode;
      const newError = validate({
        countryCode,
        disabled,
        isRequired: required,
        isValidNumber: allowInvalidPhoneNumber ? true : (itiRef.current.isValidNumber() ?? true),
        value: internalValue,
      });
      setInternalError(newError);
      if (onErrorRef.current) {
        onErrorRef.current(newError);
      }
    }
  }, [itiIsReady, internalValue, required, disabled, allowInvalidPhoneNumber]);

  const handleChangeInput = (): void => {
    if (itiIsReady && itiRef.current && intlTelInput.utils) {
      const countryData = itiRef.current.getSelectedCountryData();
      const countryCode = countryData.dialCode;
      const phoneNumber = itiRef.current.getNumber();
      const phoneNumberNoFormat = phoneNumber.replace(/[^0-9]/g, '');
      let newValue;
      if (phoneNumber.startsWith('+')) {
        newValue = `+${phoneNumberNoFormat}`;
      } else {
        newValue = `+${countryCode}${phoneNumberNoFormat}`;
      }

      if (onChangeRef.current) {
        const country = (countryData.iso2 || '').toUpperCase();
        const nationalPhoneNumber = itiRef.current.getNumber(intlTelInput.utils.numberFormat.NATIONAL);
        onChangeRef.current(newValue, country, nationalPhoneNumber);
      }

      setInternalValue(newValue);
    }
  };

  // @todo: move this into the InputField component when there is time to make sure that doesn't break anything.
  const handleBlur = useCallback(() => {
    setIsTouchedInternal(true);
    if (onBlurRef.current) {
      onBlurRef.current();
    }
  }, []);

  const errorToShow = (isTouched ? (error ?? internalError) : undefined) ?? undefined;

  const name = id;

  return (
    <div className={s.wrapper}>
      <div className={classNames(s.phoneInputWrapper, notInQuestionnaire && s.notInQuestionnaire)}>
        {notInQuestionnaire || !triggerOnChangeOnBlur ? ( // TODO: remove triggerOnChangeOnBlur once runAIAgent issue is fixed
          <Input
            ref={inputRef}
            id={id}
            compact={notInQuestionnaire}
            name={name}
            value={internalValue}
            onInput={handleChangeInput}
            placeholder={placeholder}
            label={label}
            optional={!required}
            error={errorToShow}
            inputMode='tel'
            disabled={disabled}
            onBlur={handleBlur}
            type='tel'
            testId='phoneInput'
            onFocus={onFocus}
          />
        ) : (
          <InputOnBlur
            ref={inputRef}
            id={id}
            compact={notInQuestionnaire}
            name={name}
            value={internalValue}
            placeholder={placeholder}
            onChange={handleChangeInput}
            label={label}
            optional={!required}
            error={errorToShow}
            inputMode='tel'
            disabled={disabled}
            onBlur={handleBlur}
            type='tel'
            testId='phoneInput'
            onFocus={onFocus}
          />
        )}
      </div>
    </div>
  );
};

export default PhoneInput;
